import UserService from '@/services/UserService';
import LocationService from '@/services/LocationService';
import { DeviceUtils } from '@article/pixel';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage.js';
import { startSession } from '@/compositions/useAlchemist.js';
import Tracking from '@/components/commons/Tracking.js';
import Fingerprint2 from 'fingerprintjs2'
import hash from 'object-hash'

const chronicleEndpoint = process.env.CHRONICLE_ENDPOINT

let queue = []
let attempts = 0
let features = {}
let id = ''

export default class Chronicle {
  static async init() {
    if (process.env.STOREFRONT_SERVER) { return false }

    this.loaded = false

    let fingerPrint = LocalStorage.get(LocalStorageKeys.DEVICE_FINGERPRINT)
    const session = JSON.parse(LocalStorage.get(LocalStorageKeys.ALCHEMIST_SESSION) || null)

    if(!fingerPrint) {
      const components = await Fingerprint2.getPromise()
      const newFingerPrint = hash(components)
      LocalStorage.set(LocalStorageKeys.DEVICE_FINGERPRINT, newFingerPrint)
      fingerPrint = newFingerPrint
    }

    const storedID = LocalStorage.get(LocalStorageKeys.ALCHEMIST_ID)

    if(session) {
      const isFresh = ((new Date()) - (new Date(session.lastUpdated))) < (60 * 60 * 1000)
      if(isFresh) {
        return Chronicle.startFromCache(session)
      }
    }

    // get session from GQL
    return Chronicle.startFromServer(storedID, UserService.getSessionId(), fingerPrint, DeviceUtils.isMobile())
  }

  static startFromCache(session) {
    id = session.id
    features = Chronicle.overrideFeatures(session.features)

    LocalStorage.set(LocalStorageKeys.ALCHEMIST_SESSION, JSON.stringify({
      id,
      features,
      lastUpdated: session.lastUpdated,
    }))

    this.loaded = true
    attempts = 0
    Chronicle.trackQueuedEvents()
  }

  static async startFromServer(userId, sessionId, fingerPrint, isMobile) {
    try{
      const session = await startSession(userId, sessionId, fingerPrint, isMobile);

      if(!session || !session.getAlchemistSession || !session.getAlchemistSession.session) { return false }

      id = session.getAlchemistSession.session.hashKey
      features = Chronicle.overrideFeatures(session.getAlchemistSession.session.features)

      LocalStorage.set(LocalStorageKeys.ALCHEMIST_SESSION, JSON.stringify({
        id,
        features,
        lastUpdated: (new Date()),
      }))

      LocalStorage.set(LocalStorageKeys.ALCHEMIST_ID, id)

      Chronicle.trackSofaFinderVariantStatus(features)

      attempts = 0
      Chronicle.trackQueuedEvents()
      this.loaded = true
    }catch(e) {
      if(attempts > 2) {
        Tracking.sentryCaptureError(e);
        attempts = 0
      }else{
        attempts += 1
        Chronicle.init()
      }
    }
  }

  static overrideFeatures(features) {
    const urlParams = new URLSearchParams(window.location.search);
    const overrides = urlParams.get('override-feature')
    if(overrides) {
      features[overrides.split(',')[0]] = overrides.split(',')[1]
    }

    return features
  }

  static getFeatureStatus(featureName) {
    return features[featureName]
  }

  static passiveTrack(event) {
    const events = JSON.parse(LocalStorage.get(LocalStorageKeys.ALCHEMIST_EVENTS) || '[]')
    events.push(event)
    LocalStorage.set(LocalStorageKeys.ALCHEMIST_EVENTS, JSON.stringify(events))
  }

  static markEventsAsTracked(events, source) {
    const eventsData = events.map((y) => JSON.stringify(y))
    return source.filter((x) => {
      if(eventsData.indexOf(JSON.stringify(x)) < 0) {
        return x
      }
    })
  }

  static async trackQueuedEvents() {
    if (process.env.STOREFRONT_SERVER) { return false }
    const storedEvents = JSON.parse(LocalStorage.get(LocalStorageKeys.ALCHEMIST_EVENTS) || '[]')
    const queuedEvents = JSON.parse(JSON.stringify(queue))

    const eventsToTrack = [...queuedEvents, ...storedEvents]

    if(eventsToTrack.length === 0) { return }

    const trackThis = eventsToTrack.map((x) => ({
      event: x.event,
      identityId: UserService.getSessionId(),
      sessionId: id,
      deviceId: LocalStorage.get(LocalStorageKeys.DEVICE_FINGERPRINT),
      metadata: {
        currency: LocationService.getCurrencyId(),
        browser: window.navigator.userAgent
      },
      features,
      data: x.data
    }))

    const response = await fetch(chronicleEndpoint, {
      headers: { "Content-Type": "application/json" },
      method: "POST",
      body: JSON.stringify(trackThis)
    }).then(() => {
      queue = Chronicle.markEventsAsTracked(queuedEvents, queue)

      LocalStorage.set(LocalStorageKeys.ALCHEMIST_EVENTS, JSON.stringify(
        Chronicle.markEventsAsTracked(storedEvents, JSON.parse(LocalStorage.get(LocalStorageKeys.ALCHEMIST_EVENTS) || '[]'))
      ))
      attempts = 0
    }).catch((e) => {
      if(attempts > 2) {
        Tracking.sentryCaptureError(e);
        attempts = 0
      }else{
        attempts += 1
        Chronicle.trackQueuedEvents()
      }
    });

    return response
  }

  static async track(eventName, data, passive = false) {
    if (process.env.STOREFRONT_SERVER) { return false }

    const trackThis = [{
      event: eventName,
      identityId: UserService.getSessionId(),
      sessionId: id,
      deviceId: LocalStorage.get(LocalStorageKeys.DEVICE_FINGERPRINT),
      metadata: {
        currency: LocationService.getCurrencyId(),
        browser: window.navigator.userAgent
      },
      features,
      data
    }]

    if(!this.loaded) {
      queue.push(trackThis[0])
      return
    }

    if(passive) {
      Chronicle.passiveTrack(trackThis[0])
      return
    }

    const response = await fetch(chronicleEndpoint, {
      headers: { "Content-Type": "application/json" },
      method: "POST",
      body: JSON.stringify(trackThis)
    }).then(() => {
      queue = []
      attempts = 0
    }).catch((e) => {
      if(attempts > 2) {
        Tracking.sentryCaptureError(e);
        attempts = 0
      }else{
        attempts += 1
        Chronicle.track(eventName, data)
      }
    });

    return response
  }

  static trackSofaFinderVariantStatus(features) {
    const hasAlreadyBeenTracked = JSON.parse(LocalStorage.get(LocalStorageKeys.SOFA_FINDER_VARIANT_STATUS_TRACKED) || "false")
    if(!hasAlreadyBeenTracked) {
      LocalStorage.set(LocalStorageKeys.SOFA_FINDER_VARIANT_STATUS_TRACKED, 'true')
      Tracking.track('sofa-finder-visibility', { status: features['sofa-finder-visible'] })
    }
  }
}
